import React from 'react';
import { Bone, SkeletonRegion, range } from '../../components/Skeleton';

// Placeholder shapes for student pages and blocks while their data loads
// (primitives in components/Skeleton.jsx).
// Each skeleton mirrors the real layout it stands in for, so content lands
// where the grey blocks were instead of jumping. AI work (generating, scoring,
// thinking) uses the sparkle Loader instead — see components/Loader.jsx.
//
// Colours read the theme's CSS variables with fallbacks: the app-boot
// skeleton renders before StudentApp has applied the student's theme.

const SURFACE = {
  background: 'var(--st-card, #ffffff)',
  borderColor: 'var(--st-border, #e5e7eb)',
};
const CARD = 'rounded-[var(--st-radius-card,16px)] border';
// Bones that sit on a coloured (primary) hero instead of a card.
const ON_COLOUR = { background: 'rgba(255,255,255,0.22)' };

export { Bone, SkeletonRegion };

function Card({ className = '', style, children }) {
  return <div className={`${CARD} ${className}`} style={{ ...SURFACE, ...style }}>{children}</div>;
}

/** Title + accent stroke + one-line description, like StPageHeading. */
export function HeadingBones({ action = false }) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <div className="flex-1 min-w-0 space-y-3">
        <Bone className="h-8 w-56 max-w-full rounded-lg" />
        <Bone className="h-1 w-10 rounded-full" />
        <Bone className="h-4 w-full max-w-xl" />
      </div>
      {action && <Bone className="h-10 w-32 rounded-full" />}
    </div>
  );
}

function SectionTitleBones() {
  return (
    <div className="mb-4 space-y-2">
      <Bone className="h-6 w-40 rounded-lg" />
      <Bone className="h-1 w-10 rounded-full" />
    </div>
  );
}

/** Icon tile + two text lines: the row shape most student lists use. */
function IconRow({ tile = 'w-10 h-10', trailing = null }) {
  return (
    <div className="flex items-center gap-3">
      <Bone className={`${tile} rounded-lg shrink-0`} />
      <div className="flex-1 min-w-0 space-y-2">
        <Bone className="h-4 w-3/5" />
        <Bone className="h-3 w-2/5" />
      </div>
      {trailing}
    </div>
  );
}

// ─── App shell + onboarding (full screen, before the theme exists) ──────────

/** Header + sidebar + desk content: shown while StudentApp loads the profile. */
export function AppShellSkeleton() {
  return (
    <SkeletonRegion label="Loading MyDeskey…" testId="student-app-loading" className="min-h-screen">
      <div className="min-h-screen" style={{ background: 'var(--st-bg, #ffffff)' }}>
        <div className="h-16 px-4 sm:px-6 flex items-center gap-3 border-b" style={SURFACE}>
          <Bone className="w-8 h-8 rounded-lg" />
          <Bone className="h-4 w-24 hidden sm:block" />
          <Bone className="h-9 w-40 rounded-lg ml-3" />
          <div className="flex-1" />
          <Bone className="h-9 w-24 rounded-lg hidden sm:block" />
          <Bone className="w-9 h-9 rounded-full" />
        </div>
        <div className="flex">
          <div className="hidden md:block w-60 shrink-0 py-4 px-3 border-r space-y-2" style={SURFACE}>
            {range(9).map(i => <Bone key={i} className="h-9 rounded-lg" />)}
          </div>
          <div className="flex-1 min-w-0 px-4 sm:px-8 py-6 sm:py-8">
            <DeskSkeletonBody />
          </div>
        </div>
      </div>
    </SkeletonRegion>
  );
}

/** Step header + question + choice chips: shown while onboarding loads. */
export function OnboardingSkeleton() {
  return (
    <SkeletonRegion label="Loading…" testId="onboarding-loading" className="min-h-screen flex flex-col">
      <div className="border-b" style={SURFACE}>
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-4">
          <Bone className="w-8 h-8 rounded-xl" />
          <Bone className="h-4 w-24" />
          <div className="flex-1 flex gap-1.5">
            {range(5).map(i => <Bone key={i} className="flex-1 h-[3px] rounded-full" />)}
          </div>
        </div>
      </div>
      <div className="flex-1 w-full max-w-3xl mx-auto px-6 pt-10 sm:pt-14 space-y-4">
        <Bone className="h-9 w-2/3 rounded-lg" />
        <Bone className="h-4 w-1/2" />
        <div className="pt-6 flex flex-wrap gap-2.5">
          {range(12).map(i => <Bone key={i} className="h-10 rounded-full" style={{ width: 90 + (i % 4) * 22 }} />)}
        </div>
      </div>
    </SkeletonRegion>
  );
}

// ─── Student Desk ───────────────────────────────────────────────────────────

/** Stat placeholders inside the coloured desk hero. */
export function DeskHeroStatBones() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-6" aria-hidden="true">
      {range(4).map(i => (
        <div key={i} className="space-y-2">
          <Bone className="h-7 w-12" style={ON_COLOUR} />
          <Bone className="h-3 w-16" style={ON_COLOUR} />
        </div>
      ))}
    </div>
  );
}

/** "My classes" + "New from your teachers" sections of the Student Desk. */
export function DeskSectionsSkeleton() {
  return (
    <SkeletonRegion label="Loading your school details…" testId="student-desk-loading">
      <DeskSectionsBones />
    </SkeletonRegion>
  );
}

function DeskSectionsBones() {
  return (
    <div className="space-y-10">
      <section>
        <SectionTitleBones />
        <ClassCardGrid count={3} cols="sm:grid-cols-2 lg:grid-cols-3" />
      </section>
      <section>
        <SectionTitleBones />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {range(4).map(i => <Card key={i} className="p-4"><IconRow tile="w-12 h-12" /></Card>)}
        </div>
      </section>
    </div>
  );
}

function DeskSkeletonBody() {
  return (
    <div className="space-y-10">
      <div className={`${CARD} p-6 sm:p-8 space-y-3`} style={SURFACE}>
        <Bone className="h-4 w-48" />
        <Bone className="h-12 w-64 max-w-full rounded-lg" />
      </div>
      <DeskSectionsBones />
    </div>
  );
}

// ─── MyDesk (classes → chapters) ────────────────────────────────────────────

function ClassCardGrid({ count, cols }) {
  return (
    <div className={`grid grid-cols-1 ${cols} gap-4`}>
      {range(count).map(i => (
        <Card key={i} className="p-5">
          <IconRow />
          <div className="mt-4 pt-4 border-t space-y-2" style={SURFACE}>
            <Bone className="h-3 w-20" />
            <Bone className="h-4 w-4/5" />
            <Bone className="h-3 w-3/5" />
          </div>
        </Card>
      ))}
    </div>
  );
}

/** Class card grid on MyDesk. */
export function MyDeskClassesSkeleton() {
  return (
    <SkeletonRegion label="Loading your classes…" testId="mydesk-loading">
      <ClassCardGrid count={6} cols="md:grid-cols-2 xl:grid-cols-3" />
    </SkeletonRegion>
  );
}

/** One class: back link, title, chapter cards (first one open with lessons). */
export function MyDeskClassSkeleton() {
  return (
    <SkeletonRegion label="Loading class…" testId="mydesk-class-loading" className="space-y-6">
      <div className="space-y-3">
        <Bone className="h-4 w-24" />
        <HeadingBones />
      </div>
      <div className="space-y-3">
        {range(3).map(i => (
          <Card key={i} className="overflow-hidden">
            <div className="px-5 py-4"><IconRow trailing={<Bone className="w-4 h-4 shrink-0" />} /></div>
            {i === 0 && (
              <div className="border-t px-5 py-4 space-y-2" style={SURFACE}>
                <Bone className="h-3 w-28 mb-3" />
                {range(3).map(j => (
                  <div key={j} className="p-3 rounded-lg border" style={SURFACE}>
                    <IconRow tile="w-9 h-9" trailing={<Bone className="h-9 w-24 rounded-full shrink-0" />} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </SkeletonRegion>
  );
}

// ─── Lesson (LearnNow) ──────────────────────────────────────────────────────

/** Menu rail + lessons sidebar + lesson body + tutor pane. */
export function LessonSkeleton() {
  return (
    <SkeletonRegion label="Opening lesson…" testId="learn-now-loading" className="flex h-[calc(100vh-64px)]">
      <div className="border-r shrink-0 py-3 flex flex-col items-center gap-3" style={{ ...SURFACE, width: 44 }}>
        {range(4).map(i => <Bone key={i} className="w-7 h-7 rounded-lg" />)}
      </div>
      <div className="hidden md:block border-r shrink-0 p-3 space-y-2.5" style={{ ...SURFACE, width: 260 }}>
        <Bone className="h-4 w-28 mb-4" />
        {range(8).map(i => <Bone key={i} className="h-8 rounded-lg" />)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="border-b px-4 sm:px-6 py-2.5 flex gap-2 overflow-hidden" style={SURFACE}>
          {range(6).map(i => <Bone key={i} className="h-7 w-20 rounded-full shrink-0" />)}
        </div>
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-8 space-y-4">
          <Bone className="h-9 w-3/4 rounded-lg" />
          <Bone className="h-4 w-1/3" />
          <Bone className="h-48 rounded-2xl mt-6" />
          {range(5).map(i => <Bone key={i} className={`h-4 ${i % 2 ? 'w-11/12' : 'w-full'}`} />)}
        </div>
      </div>
      <div className="hidden lg:flex flex-col border-l shrink-0 p-4 gap-3" style={{ ...SURFACE, width: 440 }}>
        <Bone className="h-8 w-40 rounded-lg" />
        <Bone className="h-16 w-4/5 rounded-2xl" />
        <Bone className="h-10 w-3/5 rounded-2xl self-end" />
        <div className="flex-1" />
        <Bone className="h-12 rounded-2xl" />
      </div>
    </SkeletonRegion>
  );
}

// ─── Assignments ────────────────────────────────────────────────────────────

function SubjectGroupBones({ rows }) {
  return (
    <div className="rounded-3xl border-2 overflow-hidden" style={SURFACE}>
      <div className="px-5 py-3 border-b" style={SURFACE}>
        <IconRow tile="w-9 h-9" trailing={<Bone className="h-6 w-16 rounded-full shrink-0" />} />
      </div>
      {range(rows).map(i => (
        <div key={i} className="px-5 py-4 border-b last:border-b-0" style={SURFACE}>
          <IconRow tile="w-8 h-8" trailing={<Bone className="h-8 w-24 rounded-full shrink-0" />} />
        </div>
      ))}
    </div>
  );
}

/** Heading, live-classes banner, filter tabs, subject groups. */
export function AssignmentsSkeleton() {
  return (
    <SkeletonRegion label="Loading assignments…" testId="assignments-loading" className="space-y-8 max-w-6xl pb-20">
      <HeadingBones />
      <section>
        <SectionTitleBones />
        <Bone className="h-32 rounded-3xl" />
      </section>
      <section>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <SectionTitleBones />
          <Bone className="h-10 w-72 max-w-full rounded-full" />
        </div>
        <div className="space-y-6">
          <SubjectGroupBones rows={3} />
          <SubjectGroupBones rows={2} />
        </div>
      </section>
    </SkeletonRegion>
  );
}

// ─── DeskeyHUB ──────────────────────────────────────────────────────────────

/** Department card grid on DeskeyHUB (heading and search render already). */
export function HubGridSkeleton() {
  return (
    <SkeletonRegion label="Loading DeskeyHUB…" testId="hub-loading">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {range(6).map(i => (
          <div key={i} className="rounded-2xl border-2 overflow-hidden" style={SURFACE}>
            <Bone className="h-1.5 rounded-none" />
            <div className="p-5 space-y-3">
              <IconRow />
              <Bone className="h-3 w-full" />
              <Bone className="h-3 w-4/5" />
            </div>
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

// ─── Insights (analytics) ───────────────────────────────────────────────────

function ChartCardBones({ chart = true }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-4">
        <Bone className="w-8 h-8 rounded-lg" />
        <Bone className="h-4 w-36" />
      </div>
      {chart ? <Bone className="h-40 rounded-xl" /> : (
        <div className="space-y-3">
          {range(4).map(i => (
            <div key={i} className="space-y-1.5">
              <Bone className="h-4 w-2/5" />
              <Bone className="h-2 w-full rounded-full" />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/** Summary tiles, trend/activity charts, strengths/weak-spots lists. */
export function AnalyticsSkeleton() {
  return (
    <SkeletonRegion label="Loading your insights…" testId="analytics-loading"
      className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
      <HeadingBones />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {range(4).map(i => (
          <Card key={i} className="p-4 space-y-2">
            <Bone className="h-3 w-20" />
            <Bone className="h-8 w-16 rounded-lg" />
          </Card>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-3"><ChartCardBones /><ChartCardBones /></div>
      <div className="grid md:grid-cols-2 gap-3"><ChartCardBones chart={false} /><ChartCardBones chart={false} /></div>
    </SkeletonRegion>
  );
}

// ─── Badges ─────────────────────────────────────────────────────────────────

/** Trophy showcase, stat cards, badge tabs and badge grid. */
export function BadgesSkeleton() {
  return (
    <SkeletonRegion label="Loading badges…" testId="badges-loading" className="space-y-10 max-w-6xl pb-20">
      <HeadingBones />
      <section>
        <SectionTitleBones />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {range(4).map(i => (
            <div key={i} className="rounded-3xl p-5 border-2 space-y-3" style={SURFACE}>
              <Bone className="w-12 h-12 rounded-xl" />
              <Bone className="h-4 w-2/3" />
              <Bone className="h-3 w-full" />
              <Bone className="h-1.5 w-full rounded-full" />
            </div>
          ))}
        </div>
      </section>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {range(3).map(i => <Card key={i} className="p-4"><IconRow tile="w-9 h-9" /></Card>)}
      </div>
      <section>
        <Bone className="h-11 w-64 max-w-full rounded-2xl mb-4" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {range(8).map(i => (
            <Card key={i} className="p-4 flex flex-col items-center gap-2">
              <Bone className="w-12 h-12 rounded-full" />
              <Bone className="h-4 w-3/4" />
              <Bone className="h-3 w-1/2" />
            </Card>
          ))}
        </div>
      </section>
    </SkeletonRegion>
  );
}

// ─── Credits ────────────────────────────────────────────────────────────────

/** Balance cards, lifetime stats and the plan grid. */
export function CreditsSkeleton() {
  return (
    <SkeletonRegion label="Loading credits…" testId="credits-loading" className="space-y-10 pb-20">
      <HeadingBones />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {range(2).map(i => (
          <Card key={i} className="p-6 space-y-4">
            <IconRow />
            <Bone className="h-10 w-32 rounded-lg" />
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {range(3).map(i => (
          <Card key={i} className="p-4 space-y-2">
            <Bone className="h-3 w-20 max-w-full" />
            <Bone className="h-6 w-16 max-w-full" />
          </Card>
        ))}
      </div>
      <section>
        <SectionTitleBones />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {range(4).map(i => (
            <div key={i} className="rounded-2xl p-5 border-2 space-y-3" style={SURFACE}>
              <Bone className="h-4 w-24" />
              <Bone className="h-9 w-20 rounded-lg" />
              <Bone className="h-3 w-full" />
              <Bone className="h-10 w-full rounded-full mt-2" />
            </div>
          ))}
        </div>
      </section>
    </SkeletonRegion>
  );
}

/** Credit pack rows inside the recharge modal. */
export function PackRowsSkeleton() {
  return (
    <SkeletonRegion label="Loading packs…" testId="recharge-packs-loading" className="space-y-2">
      {range(4).map(i => (
        <div key={i} className="rounded-xl p-3 border" style={SURFACE}>
          <IconRow tile="w-9 h-9" trailing={<Bone className="h-8 w-14 shrink-0" />} />
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** Invoice document: header, bill-to block, line items, total. */
export function InvoiceSkeleton() {
  return (
    <SkeletonRegion label="Fetching invoice…" testId="invoice-loading" className="p-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2"><Bone className="h-6 w-32" /><Bone className="h-3 w-44" /></div>
        <Bone className="h-10 w-10 rounded-full" />
      </div>
      <div className="grid grid-cols-2 gap-6">
        {range(2).map(i => (
          <div key={i} className="space-y-2"><Bone className="h-3 w-16" /><Bone className="h-4 w-36 max-w-full" /><Bone className="h-3 w-28 max-w-full" /></div>
        ))}
      </div>
      <div className="space-y-3 border-t pt-4" style={SURFACE}>
        {range(3).map(i => (
          <div key={i} className="flex justify-between gap-4"><Bone className="h-4 w-1/2" /><Bone className="h-4 w-16" /></div>
        ))}
      </div>
      <div className="flex justify-between gap-4 border-t pt-4" style={SURFACE}>
        <Bone className="h-5 w-20" /><Bone className="h-5 w-24" />
      </div>
    </SkeletonRegion>
  );
}

// ─── Lists: tests, questions, formulas, history ─────────────────────────────

/** Saved test rows on My Tests. */
export function TestRowsSkeleton({ count = 4 }) {
  return (
    <SkeletonRegion label="Loading your tests…" testId="my-quizzes-loading" className="space-y-2">
      {range(count).map(i => (
        <div key={i} className="rounded-2xl p-4 border" style={SURFACE}>
          <IconRow trailing={<Bone className="h-6 w-14 rounded-full shrink-0" />} />
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** Exam question cards (year · difficulty · type, then the question). */
export function QuestionCardsSkeleton({ count = 4, accent, label = 'Loading questions…', testId = 'questions-loading' }) {
  return (
    <SkeletonRegion label={label} testId={testId} className="space-y-2.5">
      {range(count).map(i => (
        <div key={i} className="rounded-2xl border p-4 space-y-2.5"
          style={{ ...SURFACE, ...(accent ? { borderLeft: `3px solid ${accent}` } : null) }}>
          <Bone className="h-3 w-40" />
          <Bone className="h-4 w-full" />
          <Bone className="h-4 w-3/4" />
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** Formula cards grouped under a subject label. */
export function FormulaSheetSkeleton({ accent }) {
  return (
    <SkeletonRegion label="Scanning your quiz history…" testId="formula-sheet-loading" className="space-y-6">
      {range(2).map(s => (
        <section key={s} className="space-y-2">
          <Bone className="h-3 w-24" />
          <div className="grid sm:grid-cols-2 gap-2.5">
            {range(4).map(i => (
              <div key={i} className="rounded-2xl border px-4 py-5 flex justify-center"
                style={{ ...SURFACE, ...(accent ? { borderLeft: `3px solid ${accent}` } : null) }}>
                <Bone className="h-6 w-1/2" />
              </div>
            ))}
          </div>
        </section>
      ))}
    </SkeletonRegion>
  );
}

/** A few text lines, for an answer loading inside a card. */
export function TextLinesSkeleton({ lines = 3, label = 'Loading…', testId }) {
  return (
    <SkeletonRegion label={label} testId={testId} className="space-y-2">
      {range(lines).map(i => <Bone key={i} className={`h-3 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`} />)}
    </SkeletonRegion>
  );
}

/** Quiz-history rows inside the Q&A tab. */
export function QuizHistorySkeleton() {
  return (
    <SkeletonRegion label="Loading quiz history…" testId="quiz-history-loading" >
      <div className="rounded-3xl border p-4 space-y-3" style={SURFACE}>
        <div className="flex items-center gap-2 px-1"><Bone className="w-7 h-7 rounded-lg" /><Bone className="h-4 w-36" /></div>
        {range(3).map(i => (
          <div key={i} className="rounded-xl px-3 py-2.5 border" style={SURFACE}>
            <IconRow tile="w-11 h-11" />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/** Saved generations list in a studio sidebar (MagicDesk, Super Narrate…). */
export function GenerationListSkeleton() {
  return (
    <SkeletonRegion label="Loading…" testId="generations-list-loading" className="px-4 py-3 space-y-3">
      {range(4).map(i => (
        <div key={i} className="space-y-1.5"><Bone className="h-3.5 w-4/5" /><Bone className="h-2.5 w-1/2" /></div>
      ))}
    </SkeletonRegion>
  );
}

/** A file preview area (image, PDF, video) while the file downloads. */
export function PreviewSkeleton() {
  return (
    <SkeletonRegion label="Loading preview…" testId="attachment-preview-loading" className="absolute inset-0 p-6 sm:p-10 flex">
      <Bone className="flex-1 rounded-xl" />
    </SkeletonRegion>
  );
}

/** Quiz title and question card while a quiz attempt loads. */
export function QuizAttemptSkeleton() {
  return (
    <SkeletonRegion label="Loading quiz…" testId="quiz-attempt-loading" className="max-w-4xl mx-auto px-4 py-8 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <Bone className="h-9 w-64 max-w-full rounded-xl" />
        <Bone className="h-9 w-24 rounded-full" />
      </div>
      <Bone className="h-2 w-full rounded-full" />
      <Card className="p-6 space-y-5">
        <Bone className="h-3 w-28" />
        <Bone className="h-5 w-full" />
        <Bone className="h-5 w-3/4" />
        <div className="space-y-2.5 pt-2">
          {range(4).map(i => <Bone key={i} className="h-12 rounded-xl" />)}
        </div>
      </Card>
    </SkeletonRegion>
  );
}
